import type { ScoringConfig } from '@/lib/api';

/**
 * WHAT MAKES A BAND SET WELL-FORMED — the console's copy of the server's rule.
 *
 * ### Why there are two copies, and what stops them drifting
 *
 * `bandCoverageIssues` in `server/src/contract/scoringConfig.ts` is the
 * authority: it runs inside `ScoringConfig`'s `superRefine`, so a config that
 * fails it cannot be saved by any client, including this one. This copy exists
 * because the band editor's whole value is telling an operator about a gap while
 * they are typing the number that made it, and a server round-trip per keystroke
 * is not an editor.
 *
 * Duplicated arithmetic is normally how two answers to one question appear (see
 * `qcDetectors` in `plugins/shared.ts`, which refuses to be re-derived for
 * exactly that reason). What makes it acceptable here is that the two are pinned
 * together by a test — `src/test/scoring-config.test.ts` runs a fixture set
 * through BOTH implementations and fails the build on any disagreement — and
 * that the server's copy is the one that decides. The worst this can do when it
 * drifts is warn about a config that would have saved, or stay quiet about one
 * the server then rejects with the same sentence.
 *
 * ### The rules
 *
 * Both ends of a band are INCLUSIVE (`bandFor` matches `value >= min && value <=
 * max`), and scores are integers, so bands touch at consecutive integers —
 * `0–39` then `40–69` — rather than at a shared boundary:
 *
 *   - `next.min <= prev.max` — overlap. A value in it resolves by array order.
 *   - `next.min > prev.max + 1` — a gap. A value in it falls back to `bands[0]`
 *     and is reported under the wrong label with nothing saying so.
 *
 * The second is why this was worth building. The schema used to check only that
 * something covered 0 and something reached 100, so `[0–30]` and `[60–100]`
 * saved cleanly and every score from 31 to 59 was silently labelled with the
 * first band.
 */
export type BandRow = ScoringConfig['bands'][number];

export function bandCoverageIssues(bands: readonly BandRow[]): string[] {
  const issues: string[] = [];
  if (bands.length === 0) return ['at least one interpretation band is required'];

  for (const band of bands) {
    if (band.min > band.max) {
      issues.push(`band '${band.label}' starts at ${band.min} and ends at ${band.max}`);
    }
  }

  const sorted = [...bands].sort((a, b) => a.min - b.min || a.max - b.max);

  if (sorted[0]!.min > 0) issues.push(`no band covers scores below ${sorted[0]!.min}`);

  const ceiling = Math.max(...bands.map((b) => b.max));
  if (ceiling < 100) issues.push(`no band covers scores above ${ceiling}`);

  for (let i = 1; i < sorted.length; i += 1) {
    const previous = sorted[i - 1]!;
    const band = sorted[i]!;
    if (band.min <= previous.max) {
      issues.push(
        `bands '${previous.label}' (${previous.min}–${previous.max}) and '${band.label}' ` +
          `(${band.min}–${band.max}) overlap — a score in the overlap has two bands`,
      );
    } else if (band.min > previous.max + 1) {
      issues.push(
        `nothing covers ${previous.max + 1}–${band.min - 1}, between '${previous.label}' and '${band.label}'`,
      );
    }
  }

  return issues;
}

/**
 * The 0–100 track as proportional segments, gaps included.
 *
 * A GAP IS A SEGMENT, not an absence — that is the point of drawing this at all.
 * A strip that rendered only the authored bands would close up around a gap and
 * look complete, which is precisely the state the operator needs to see. So the
 * uncovered stretches come back as `{ band: null }` and the strip paints them as
 * a hazard rather than as background.
 *
 * Overlaps cannot be drawn as segments on one track — two bands claiming 40–50
 * would have to occupy the same pixels — so they are reported by
 * `bandCoverageIssues` as text and the strip renders the sorted ranges as they
 * fall. The text is the carrier there, and the strip does not pretend otherwise.
 */
export interface StripSegment {
  /** Start of the segment in track units, `0`–`TRACK`. */
  from: number;
  /** End, exclusive. */
  to: number;
  /** `null` for an uncovered stretch. */
  band: BandRow | null;
}

/**
 * THE TRACK IS 101 UNITS WIDE, NOT 100 — one per reachable score, 0 through 100.
 *
 * Band ranges are inclusive at both ends, so `0–39` and `40–69` are contiguous
 * even though 39 ≠ 40. Drawing each band from `min` to `max` on a 100-wide track
 * puts a one-unit sliver between every pair of adjacent bands, and a strip whose
 * job is to show gaps must not manufacture three fake ones. Mapping each band to
 * the half-open `[min, max+1)` over 101 units removes the slivers AND keeps
 * every band's width proportional to the number of scores it actually claims.
 */
export const TRACK = 101;

export function stripSegments(bands: readonly BandRow[]): StripSegment[] {
  const clamp = (n: number) => Math.min(Math.max(n, 0), TRACK);
  const sorted = [...bands]
    .filter((b) => b.max >= b.min)
    .sort((a, b) => a.min - b.min || a.max - b.max);

  const segments: StripSegment[] = [];
  let cursor = 0;

  for (const band of sorted) {
    const start = clamp(band.min);
    const end = clamp(band.max + 1);

    if (start > cursor) segments.push({ from: cursor, to: start, band: null });

    // `Math.max(cursor, start)` is what an overlap collapses to: the strip
    // cannot draw two bands over one stretch, so the earlier one keeps the
    // pixels and `bandCoverageIssues` reports the overlap in words. The text is
    // the carrier there and this does not pretend otherwise.
    const from = Math.max(cursor, start);
    if (end > from) segments.push({ from, to: end, band });
    cursor = Math.max(cursor, end);
  }

  if (cursor < TRACK) segments.push({ from: cursor, to: TRACK, band: null });
  return segments;
}
